import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useObjectCard } from '../context/ObjectCardProvider';
import vconst from 'utils/helpers/structs/vconst';
import { useEffect, useMemo, useState } from 'react';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { useToast } from 'context/ToastContext';
import { useAuth } from 'context/AuthContext';
import * as apiObject from 'api/apiObjects';
import ObjectPayStatus from '../../ObjectsList/ObjectPayStatus/ObjectPayStatus';
import {
    getObjectPaymentsCoverageRatio,
    isObjectPaymentsCovered,
    getObjectDebtAmount,
} from '../../ObjectsList/helpers/objectPayCoverage';
import { hasUserAccess, canViewSales, hidesPurchasePrices } from 'utils/helpers/access/hasUserAccess';
import OffHistoryPrice from '../OffHistoryPrice';
import OffHistoryDeadline from '../../OffHistoryDeadline';
import { getActualObjectCost } from '../OffHistoryPrice/model';
import ObjectAnalyticsDash from '../ObjectAnalyticsDash';
import { debtAmountInLocal, getObjectFx, useObjectDebtCurrency } from '../ObjectAnalyticsDash/model';
import SolarIcon from 'components/icons/SolarIcon';
import { TaskTypeStatus } from '@econtractor/types';
import { isOtherTeamIncome } from '../model/isOtherTeamIncome';

const objectTermStatus = (objectStatus) => {
    if (objectStatus === vconst.objectFinished || objectStatus === vconst.objectFinishedTeam) {
        return TaskTypeStatus.COMPLETED;
    }
    if (objectStatus === vconst.objectReject) return TaskTypeStatus.CANCELLED;
    return TaskTypeStatus.INWORK;
};

const ObjectcardInfo = ({ priceSummary, setPriceSummary }) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { auth } = useAuth();
    const { objectId, vObject, vObjectInfo, setVObject, setShowSpinner, isEditLocked, onlySale, onlyService, isSaleLike } = useObjectCard();
    const { currency: debtCurrency, toggle: toggleDebtCurrency } = useObjectDebtCurrency(Number(objectId) || 0);
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const showPayments = Boolean(params.objectPaymentControl);
    const canManualPayStatus = Boolean(
        params.objectPaymentControl && params.manualPayStatus,
    );
    const [payStatusBusy, setPayStatusBusy] = useState(false);
    const [showHistoryPrice, setShowHistoryPrice] = useState(false);
    const [showHistoryDeadline, setShowHistoryDeadline] = useState(false);
    const hideProfitability = hidesPurchasePrices(auth?.user);
    const materialsEnabled = !onlyService;
    const servicesEnabled = !onlySale;
    const extraEnabled = !isSaleLike;
    const otherEnabled = !isSaleLike;
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = rate === 1;
    const object = vObject?.object;
    const recordEdit = object?.myAccess
        ? object.myAccess === 'edit'
        : (isSaleLike
            ? canViewSales(auth?.user) || hasUserAccess(auth?.user, 'objectCreate')
            : hasUserAccess(auth?.user, 'objectCreate'));
    const canChangePrice = recordEdit && !isEditLocked;
    const priceObject = object?.objectPrice;
    const priceObjectRate = object?.objectPriceRate;
    const expendTeams = vObject.expendTeams;
    const expends = vObject.expends;

    const rawMaterials = vObjectInfo?.prodPrice || 0;
    const rawMaterialsRate = vObjectInfo?.prodPriceRate || 0;
    const priceMaterials = materialsEnabled ? rawMaterials : 0;
    const priceMaterialsRate = materialsEnabled ? rawMaterialsRate : 0;
    const priceMaterialsSale = materialsEnabled ? (vObjectInfo?.prodPriceSale || 0) - rawMaterials : 0;
    const priceMaterialsSaleRate = materialsEnabled
        ? (vObjectInfo?.prodPriceSaleRate || 0) - rawMaterialsRate
        : 0;

    let servicesAmount = 0;
    let servicesAmountRate = 0;
    let servicesSale = 0;
    let servicesSaleRate = 0;
    let extraAmount = 0;
    let extraAmountRate = 0;
    let extraSale = 0;
    let extraSaleRate = 0;
    expendTeams?.forEach((expend) => {
        if (expend.income_status != vconst.incomeStatActive) return;
        const count = Number(expend.income_count ?? expend.incomeCount ?? 1) || 1;
        const priceSale = Number(expend.income_price_sale ?? expend.incomePriceSale ?? 0);
        const priceSaleRate = Number(expend.income_price_sale_rate ?? expend.incomePriceSaleRate ?? 0);
        const amount = Number(expend.income_amount) || 0;
        const amountRate = Number(expend.income_amount_rate) || 0;
        if (isOtherTeamIncome(expend)) {
            if (!extraEnabled) return;
            extraAmount += amount;
            extraAmountRate += amountRate;
            extraSale += count * priceSale;
            extraSaleRate += count * priceSaleRate;
            return;
        }
        if (!servicesEnabled) return;
        servicesAmount += amount;
        servicesAmountRate += amountRate;
        servicesSale += count * priceSale;
        servicesSaleRate += count * priceSaleRate;
    });
    const priceServicesSale = servicesSale - servicesAmount;
    const priceServicesSaleRate = servicesSaleRate - servicesAmountRate;
    const priceExtraSale = extraSale - extraAmount;
    const priceExtraSaleRate = extraSaleRate - extraAmountRate;

    let otherAmount = 0;
    let otherAmountRate = 0;
    if (otherEnabled) {
        expends?.forEach((expend) => {
            if (Number(expend.expendStatus) !== vconst.incomeStatActive) return;
            otherAmount += Number(expend.expendAmount) || 0;
            otherAmountRate += Number(expend.expendAmountRate) || 0;
        });
    }

    useEffect(() => {
        setPriceSummary(priceObject - priceMaterials - servicesAmount - extraAmount + otherAmount);
    }, [vObjectInfo, vObject, priceObject, priceMaterials, servicesAmount, extraAmount, otherAmount]);

    const actualCost = useMemo(
        () => getActualObjectCost(vObject, vObjectInfo),
        [vObject, vObjectInfo],
    );

    const paidUsd = Number(object?.paidUsd || 0);
    const paidUah = Number(object?.paidUah || 0);
    const hasPayments = paidUsd !== 0 || paidUah !== 0;
    const coverageParams = {
        objectPrice: priceObject,
        objectPriceRate: priceObjectRate,
        paidUsd,
        paidUah,
        rate,
    };
    const paymentsCovered = isObjectPaymentsCovered(coverageParams);
    const coverageRatio = getObjectPaymentsCoverageRatio(coverageParams);
    const coveragePercent = Math.min(100, Math.round(Math.max(0, coverageRatio) * 100));
    const debtAmount = getObjectDebtAmount(coverageParams);
    const objectFx = !isUahMode ? getObjectFx(priceObject, priceObjectRate) : null;
    const debtAmountLocal = objectFx != null ? debtAmountInLocal(debtAmount, objectFx) : null;
    const paymentsStatusLabel = paymentsCovered
        ? t('infoItems.objectPaymentsCovered')
        : t('infoItems.objectPaymentsPartial');
    const paymentsStatusIcon = paymentsCovered
        ? 'bi-check-circle-fill'
        : 'bi-hourglass-split';

    const handlePayStatusChange = async (payStatus) => {
        if (!objectId || payStatusBusy) return;
        setPayStatusBusy(true);
        setShowSpinner?.(true);
        try {
            const res = await apiObject.setPayStatus(objectId, payStatus);
            if (res) {
                setVObject((prev) => {
                    if (!prev?.object) return prev;
                    return {
                        ...prev,
                        object: {
                            ...prev.object,
                            objectPayStatus: payStatus,
                        },
                    };
                });
                showToast(t('messPayStatusChanged'), 'success');
            } else {
                showToast(t('common:messError'), 'danger');
            }
        } finally {
            setPayStatusBusy(false);
            setShowSpinner?.(false);
        }
    };

    const handlePriceChanged = async (nextPrice, nextRate) => {
        setVObject((prev) => {
            if (!prev?.object) return prev;
            return {
                ...prev,
                object: {
                    ...prev.object,
                    objectPrice: nextPrice,
                    objectPriceRate: nextRate,
                },
            };
        });
    };

    const handleDeadlineChanged = async (nextDeadline) => {
        setVObject((prev) => {
            if (!prev?.object) return prev;
            return {
                ...prev,
                object: {
                    ...prev.object,
                    objectDeadline: nextDeadline,
                },
            };
        });
    };

    const materialsCost = Math.abs(Number(priceMaterials) || 0);
    const servicesCost = Math.abs(Number(servicesAmount) || 0);
    const extraCost = Math.abs(Number(extraAmount) || 0);
    const otherExpense = otherAmount < 0 ? -otherAmount : 0;
    const profitValue = Number(priceSummary) || 0;
    const dashSegments = [
        {
            key: 'materials',
            label: t('infoItems.objectMaterials'),
            value: materialsCost,
            tone: 'materials',
            disabled: !materialsEnabled,
        },
        {
            key: 'services',
            label: t('infoItems.objectServices'),
            value: servicesCost,
            tone: 'services',
            disabled: !servicesEnabled,
        },
        {
            key: 'extra',
            label: t('infoItems.objectExtra'),
            value: extraCost,
            tone: 'extra',
            disabled: !extraEnabled,
        },
        {
            key: 'other',
            label: t('infoItems.objectOther'),
            value: otherExpense,
            tone: 'other',
            disabled: !otherEnabled,
        },
        {
            key: 'profit',
            label: t('infoItems.objectProfitability'),
            value: Math.abs(profitValue),
            tone: profitValue < 0 ? 'loss' : 'profit',
            bar: profitValue > 0,
        },
    ];

    return (
        <>
        <OffHistoryPrice
            show={showHistoryPrice}
            setShow={setShowHistoryPrice}
            objectId={Number(objectId)}
            objectPrice={Number(priceObject) || 0}
            objectPriceRate={Number(priceObjectRate) || 0}
            onSuccess={handlePriceChanged}
        />
        <OffHistoryDeadline
            show={showHistoryDeadline}
            setShow={setShowHistoryDeadline}
            objectId={Number(objectId)}
            objectDeadline={Number(object?.objectDeadline) || 0}
            onSuccess={handleDeadlineChanged}
        />
        <Card className="object-analytics-card object-analytics-card--dash">
            <Card.Body className="object-analytics-card__body p-0">
                <div className="object-analytics-card__header">
                    <h5 className="object-analytics-card__title">
                        <SolarIcon name="graph-up-arrow" className="object-analytics-card__title-icon" aria-hidden="true" />
                        {t(
                            onlyService
                                ? 'titleServiceSaleAnalytics'
                                : onlySale
                                  ? 'titleSaleAnalytics'
                                  : 'titleAnalytics',
                        )}
                    </h5>
                </div>
                <ObjectAnalyticsDash
                    isUahMode={isUahMode}
                    profitability={profitValue}
                    profitabilityLabel={t('infoItems.objectProfitability')}
                    price={Number(priceObject) || 0}
                    priceRateAmount={Number(priceObjectRate) || 0}
                    actualCostLabel={t('infoItems.objectActualCost')}
                    actualCostAmount={Number(actualCost) || 0}
                    actualCostDisabled={isSaleLike}
                    segments={dashSegments}
                    priceRow={{
                        key: 'price',
                        icon: 'currency-dollar',
                        label: t('infoItems.objectPrice'),
                        amount: Number(priceObject) || 0,
                        rateTitle: Number(priceObjectRate) || 0,
                        actionLabel: canChangePrice ? t('common:buttons.change') : undefined,
                        onAction: canChangePrice ? () => setShowHistoryPrice(true) : undefined,
                    }}
                    metrics={[
                        {
                            key: 'materials',
                            icon: 'box-seam',
                            label: t('infoItems.objectMaterials'),
                            amount: hideProfitability ? (vObjectInfo?.prodPriceSale || 0) : -materialsCost,
                            rateTitle: hideProfitability ? (vObjectInfo?.prodPriceSaleRate || 0) : (Number(priceMaterialsRate) || 0),
                            secondaryAmount: hideProfitability ? undefined : priceMaterialsSale,
                            secondaryRateTitle: priceMaterialsSaleRate,
                            disabled: !materialsEnabled,
                        },
                        {
                            key: 'services',
                            icon: 'people',
                            label: t('infoItems.objectServices'),
                            amount: hideProfitability ? servicesSale : -servicesCost,
                            rateTitle: hideProfitability ? servicesSaleRate : (Number(servicesAmountRate) || 0),
                            secondaryAmount: hideProfitability ? undefined : priceServicesSale,
                            secondaryRateTitle: priceServicesSaleRate,
                            disabled: !servicesEnabled,
                        },
                        {
                            key: 'extra',
                            icon: 'inbox',
                            label: t('infoItems.objectExtra'),
                            amount: -extraCost,
                            rateTitle: Number(extraAmountRate) || 0,
                            secondaryAmount: priceExtraSale,
                            secondaryRateTitle: priceExtraSaleRate,
                            disabled: !extraEnabled || hideProfitability,
                        },
                        {
                            key: 'other',
                            icon: 'transfer',
                            label: t('infoItems.objectOther'),
                            amount: otherAmount,
                            rateTitle: otherAmountRate,
                            disabled: !otherEnabled || hideProfitability,
                        },
                    ]}
                    payments={
                        showPayments
                            ? {
                                  hasPayments,
                                  paidUsd,
                                  paidUah,
                                  coveragePercent,
                                  paymentsCovered,
                                  debtAmount,
                                  debtAmountLocal,
                                  debtCurrency,
                                  onToggleDebtCurrency: debtAmountLocal != null ? toggleDebtCurrency : undefined,
                                  statusLabel: paymentsStatusLabel,
                                  statusIcon: paymentsStatusIcon,
                              }
                            : null
                    }
                    payStatus={
                        canManualPayStatus ? (
                            <>
                                <span className="object-analytics-dash__tile-label object-analytics-dash__label-with-icon text-muted">
                                    <SolarIcon name="credit-card" aria-hidden="true" />
                                    {t('fields.payStatus')}
                                </span>
                                <ObjectPayStatus
                                    objectId={Number(objectId)}
                                    payStatus={object?.objectPayStatus}
                                    objectPrice={priceObject}
                                    objectPriceRate={priceObjectRate}
                                    paidUsd={paidUsd}
                                    paidUah={paidUah}
                                    rate={rate}
                                    editable={!isEditLocked}
                                    showCoveredHint={false}
                                    disabled={payStatusBusy || isEditLocked}
                                    onChange={handlePayStatusChange}
                                />
                            </>
                        ) : null
                    }
                    hideProfitability={hideProfitability}
                    deadline={{
                        dateStart: object?.objectCreated,
                        dateEnd: object?.objectDeadline,
                        taskStatus: objectTermStatus(object?.objectStatus),
                        canChange: canChangePrice,
                        onChange: () => setShowHistoryDeadline(true),
                        disabled: isSaleLike,
                    }}
                />
            </Card.Body>
        </Card>
        </>
    );
};

export default ObjectcardInfo;
